import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { PageHeader, PageBody, fmtMoney } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, FileText, EnvelopeSimple, Trash, ArrowLeft, Receipt } from "@phosphor-icons/react";

/**
 * Reusable Party page for Customers (kind="customer") and Suppliers (kind="supplier").
 */
export default function PartyPage({ kind }) {
  const label = kind === "customer" ? "Customer" : "Supplier";
  const plural = kind === "customer" ? "Customers" : "Suppliers";
  const partyPath = kind === "customer" ? "customers" : "suppliers";
  const receivableLabel = kind === "customer" ? "Outstanding Receivable" : "Outstanding Payable";
  const arApType = kind === "customer" ? "AR" : "AP";
  const cnType = kind === "customer" ? "AR_CN" : "AP_CN";

  const { activeCompany, formatApiError } = useAuth();
  const [list, setList] = useState([]);
  const [selected, setSelected] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [scope, setScope] = useState("all"); // all | outstanding
  const [accounts, setAccounts] = useState([]);
  const [openCreate, setOpenCreate] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", tax_id: "", notes: "" });
  const [openInvoice, setOpenInvoice] = useState(false);
  const [openCN, setOpenCN] = useState(false);
  const [invoiceForm, setInvoiceForm] = useState(initialInvoiceForm());

  function initialInvoiceForm() {
    return {
      date: new Date().toISOString().slice(0, 10),
      due_date: "",
      invoice_number: "",
      ar_ap_account_id: "",
      tax_amount: "",
      notes: "",
      lines: [{ description: "", quantity: 1, unit_price: "", account_id: "" }],
    };
  }

  const loadList = async () => {
    if (!activeCompany) return;
    const [l, a] = await Promise.all([
      api.get(`/companies/${activeCompany.id}/${partyPath}`),
      api.get(`/companies/${activeCompany.id}/accounts`),
    ]);
    setList(l.data);
    setAccounts(a.data);
  };

  const loadDetail = async (party, s = scope) => {
    if (!activeCompany || !party) return;
    const [detail, txns] = await Promise.all([
      api.get(`/companies/${activeCompany.id}/${partyPath}/${party.id}`),
      api.get(`/companies/${activeCompany.id}/${partyPath}/${party.id}/transactions?scope=${s}`),
    ]);
    setSelected(detail.data);
    setLedger(txns.data);
  };

  useEffect(() => {
    loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCompany?.id, kind]);

  useEffect(() => {
    if (selected) loadDetail(selected, scope);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope]);

  const createParty = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    try {
      await api.post(`/companies/${activeCompany.id}/${partyPath}`, form);
      toast.success(`${label} created`);
      setOpenCreate(false);
      setForm({ name: "", email: "", phone: "", address: "", tax_id: "", notes: "" });
      loadList();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const removeParty = async (party) => {
    if (!window.confirm(`Delete ${party.name}? Deactivates if there are invoices.`)) return;
    try {
      await api.delete(`/companies/${activeCompany.id}/${partyPath}/${party.id}`);
      toast.success("Removed");
      if (selected?.id === party.id) setSelected(null);
      loadList();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const arApAccounts = useMemo(
    () => accounts.filter((a) => (arApType === "AR" ? a.code === "1200" : a.code === "2000") || a.type === (arApType === "AR" ? "asset" : "liability")),
    [accounts, arApType]
  );
  const lineAccounts = useMemo(
    () => accounts.filter((a) => a.type === (arApType === "AR" ? "revenue" : "expense")),
    [accounts, arApType]
  );

  const setLine = (idx, key, value) => {
    const lines = [...invoiceForm.lines];
    lines[idx] = { ...lines[idx], [key]: value };
    setInvoiceForm({ ...invoiceForm, lines });
  };
  const addLine = () => setInvoiceForm({ ...invoiceForm, lines: [...invoiceForm.lines, { description: "", quantity: 1, unit_price: "", account_id: "" }] });
  const removeLine = (idx) => setInvoiceForm({ ...invoiceForm, lines: invoiceForm.lines.filter((_, i) => i !== idx) });

  const invoiceTotal = useMemo(() => {
    const sub = invoiceForm.lines.reduce((s, l) => s + Number(l.quantity || 0) * Number(l.unit_price || 0), 0);
    return sub + Number(invoiceForm.tax_amount || 0);
  }, [invoiceForm]);

  const submitInvoiceOrCN = async (isCN) => {
    if (!selected) return;
    const payload = {
      type: isCN ? cnType : arApType,
      counterparty: selected.name,
      date: invoiceForm.date,
      due_date: invoiceForm.due_date || undefined,
      invoice_number: invoiceForm.invoice_number || undefined,
      ar_ap_account_id: invoiceForm.ar_ap_account_id,
      tax_amount: Number(invoiceForm.tax_amount || 0),
      notes: invoiceForm.notes || undefined,
      lines: invoiceForm.lines.map((l) => ({
        description: l.description,
        quantity: Number(l.quantity || 0),
        unit_price: Number(l.unit_price || 0),
        account_id: l.account_id,
      })),
    };
    if (kind === "customer") payload.customer_id = selected.id;
    else payload.supplier_id = selected.id;

    try {
      if (isCN) {
        // Use dedicated credit-note endpoint (nicer for tracking)
        await api.post(`/companies/${activeCompany.id}/credit-notes`, {
          kind,
          party_id: selected.id,
          date: payload.date,
          invoice_number: payload.invoice_number,
          lines: payload.lines,
          ar_ap_account_id: payload.ar_ap_account_id,
          tax_amount: payload.tax_amount,
          notes: payload.notes,
        });
        toast.success(kind === "customer" ? "Credit note created" : "Debit note created");
      } else {
        await api.post(`/companies/${activeCompany.id}/invoices`, payload);
        toast.success(`${arApType === "AR" ? "Invoice" : "Bill"} created`);
      }
      setOpenInvoice(false);
      setOpenCN(false);
      setInvoiceForm(initialInvoiceForm());
      loadDetail(selected);
      loadList();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const emailStatement = async () => {
    if (!selected) return;
    const to = window.prompt(`Send statement to (email):`, selected.email || "");
    if (!to) return;
    const message = window.prompt(`Optional message (leave blank for default):`, "") || "";
    try {
      const { data } = await api.post(
        `/companies/${activeCompany.id}/${partyPath}/${selected.id}/statement/email`,
        { to, message }
      );
      toast.success(`Emailed to ${data.to}`);
    } catch (e) {
      const detail = formatApiError(e.response?.data?.detail) || e.message;
      if (e.response?.status === 503) {
        toast.error("Email not configured. Ask an admin to set RESEND_API_KEY.");
      } else {
        toast.error(detail);
      }
    }
  };

  const downloadStatementPdf = () => {
    if (!selected) return;
    const url = `${api.defaults.baseURL}/companies/${activeCompany.id}/${partyPath}/${selected.id}/statement.pdf`;
    window.open(url, "_blank");
  };

  if (!activeCompany) return <PageBody><div className="overline">Select a company</div></PageBody>;

  // ---------- Detail View ----------
  if (selected) {
    return (
      <>
        <PageHeader
          eyebrow={label}
          title={selected.name}
          description={selected.email || selected.phone || `Account · ${label} ledger`}
          actions={
            <>
              <Button
                variant="ghost"
                onClick={() => { setSelected(null); setLedger([]); }}
                data-testid="party-back-btn"
                className="rounded-sm font-mono text-xs uppercase tracking-wider"
              >
                <ArrowLeft size={14} className="mr-1" /> Back
              </Button>
              <Button
                onClick={() => { setInvoiceForm(initialInvoiceForm()); setOpenInvoice(true); }}
                data-testid="party-new-invoice-btn"
                className="rounded-sm font-mono text-xs uppercase tracking-wider"
              >
                <Plus size={14} className="mr-1" /> New {arApType === "AR" ? "Invoice" : "Bill"}
              </Button>
              <Button
                variant="outline"
                onClick={() => { setInvoiceForm(initialInvoiceForm()); setOpenCN(true); }}
                data-testid="party-new-cn-btn"
                className="rounded-sm font-mono text-xs uppercase tracking-wider"
              >
                <Receipt size={14} className="mr-1" /> {kind === "customer" ? "Credit Note" : "Debit Note"}
              </Button>
              <Button
                variant="outline"
                onClick={emailStatement}
                data-testid="party-email-btn"
                className="rounded-sm font-mono text-xs uppercase tracking-wider"
              >
                <EnvelopeSimple size={14} className="mr-1" /> Email Statement
              </Button>
              <Button
                variant="outline"
                onClick={downloadStatementPdf}
                data-testid="party-print-btn"
                className="rounded-sm font-mono text-xs uppercase tracking-wider"
              >
                <FileText size={14} className="mr-1" /> Download PDF
              </Button>
            </>
          }
        />

        <PageBody>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <StatCard label={receivableLabel} value={fmtMoney(selected.outstanding, activeCompany.currency)} testId="party-stat-outstanding" />
            <StatCard label="Total Billed" value={fmtMoney(selected.total_billed, activeCompany.currency)} testId="party-stat-billed" />
            <StatCard label="Total Received" value={fmtMoney(selected.total_received, activeCompany.currency)} testId="party-stat-received" />
            <StatCard label="Open Invoices" value={String(selected.open_invoices ?? 0)} testId="party-stat-open" />
          </div>

          <Tabs value={scope} onValueChange={setScope}>
            <TabsList className="rounded-sm">
              <TabsTrigger value="outstanding" data-testid="scope-outstanding" className="font-mono uppercase text-xs tracking-wider">Outstanding Only</TabsTrigger>
              <TabsTrigger value="all" data-testid="scope-all" className="font-mono uppercase text-xs tracking-wider">Full History</TabsTrigger>
            </TabsList>
          </Tabs>

          <div className="border border-border bg-card">
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-muted/40">
                <tr className="text-left">
                  <Th>Date</Th>
                  <Th>Type</Th>
                  <Th>Reference</Th>
                  <Th>Description</Th>
                  <Th className="text-right">Debit</Th>
                  <Th className="text-right">Credit</Th>
                  <Th className="text-right pr-4">Balance</Th>
                </tr>
              </thead>
              <tbody>
                {ledger.map((e, idx) => (
                  <tr key={idx} data-testid={`ledger-row-${idx}`} className="border-b border-border last:border-b-0">
                    <Td className="num">{e.date}</Td>
                    <Td>{e.type}</Td>
                    <Td className="font-mono">{e.ref}</Td>
                    <Td>{e.description}</Td>
                    <Td className="text-right num">{e.debit ? fmtMoney(e.debit, activeCompany.currency) : ""}</Td>
                    <Td className="text-right num">{e.credit ? fmtMoney(e.credit, activeCompany.currency) : ""}</Td>
                    <Td className="text-right pr-4 num">{fmtMoney(e.balance ?? (e.debit - e.credit), activeCompany.currency)}</Td>
                  </tr>
                ))}
                {ledger.length === 0 && (
                  <tr><td colSpan={7} className="text-center py-8 text-muted-foreground">No {scope === "outstanding" ? "outstanding items" : "transactions"} yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </PageBody>

        {/* Invoice / CN Dialog */}
        <Dialog open={openInvoice || openCN} onOpenChange={(v) => { if (!v) { setOpenInvoice(false); setOpenCN(false); } }}>
          <DialogContent className="rounded-sm max-w-3xl max-h-[90vh] overflow-auto">
            <DialogHeader>
              <DialogTitle className="font-serif-display text-2xl">
                {openCN
                  ? (kind === "customer" ? "New Credit Note" : "New Debit Note")
                  : `New ${arApType === "AR" ? "Invoice" : "Bill"}`}
              </DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-4 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-widest">Date</Label>
                <Input type="date" value={invoiceForm.date} onChange={(e) => setInvoiceForm({ ...invoiceForm, date: e.target.value })} className="rounded-sm font-mono" data-testid="party-inv-date" />
              </div>
              {!openCN && (
                <div>
                  <Label className="text-xs uppercase tracking-widest">Due</Label>
                  <Input type="date" value={invoiceForm.due_date} onChange={(e) => setInvoiceForm({ ...invoiceForm, due_date: e.target.value })} className="rounded-sm font-mono" data-testid="party-inv-due" />
                </div>
              )}
              <div>
                <Label className="text-xs uppercase tracking-widest">{openCN ? "CN #" : "Invoice #"}</Label>
                <Input value={invoiceForm.invoice_number} onChange={(e) => setInvoiceForm({ ...invoiceForm, invoice_number: e.target.value })} className="rounded-sm font-mono" data-testid="party-inv-number" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">{arApType === "AR" ? "AR Account" : "AP Account"}</Label>
                <Select value={invoiceForm.ar_ap_account_id} onValueChange={(v) => setInvoiceForm({ ...invoiceForm, ar_ap_account_id: v })}>
                  <SelectTrigger data-testid="party-inv-arap" className="rounded-sm font-mono"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {arApAccounts.map((a) => (<SelectItem key={a.id} value={a.id} className="font-mono">{a.code} · {a.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="border border-border">
              <div className="grid grid-cols-12 gap-2 px-3 py-2 border-b border-border bg-muted/40 overline">
                <div className="col-span-4">Description</div>
                <div className="col-span-4">{arApType === "AR" ? "Revenue Account" : "Expense Account"}</div>
                <div className="col-span-1 text-right">Qty</div>
                <div className="col-span-2 text-right">Price</div>
                <div className="col-span-1" />
              </div>
              {invoiceForm.lines.map((l, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 px-3 py-2 border-b border-border last:border-b-0">
                  <Input value={l.description} onChange={(e) => setLine(idx, "description", e.target.value)} className="col-span-4 rounded-sm font-mono" data-testid={`party-line-desc-${idx}`} />
                  <div className="col-span-4">
                    <Select value={l.account_id} onValueChange={(v) => setLine(idx, "account_id", v)}>
                      <SelectTrigger className="rounded-sm font-mono h-9" data-testid={`party-line-acc-${idx}`}><SelectValue placeholder="Account" /></SelectTrigger>
                      <SelectContent>
                        {lineAccounts.map((a) => (<SelectItem key={a.id} value={a.id} className="font-mono">{a.code} · {a.name}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Input type="number" step="1" value={l.quantity} onChange={(e) => setLine(idx, "quantity", e.target.value)} className="col-span-1 rounded-sm font-mono text-right" data-testid={`party-line-qty-${idx}`} />
                  <Input type="number" step="0.01" value={l.unit_price} onChange={(e) => setLine(idx, "unit_price", e.target.value)} className="col-span-2 rounded-sm font-mono text-right" data-testid={`party-line-price-${idx}`} />
                  <Button size="sm" variant="ghost" onClick={() => removeLine(idx)} className="col-span-1"><Trash size={14} /></Button>
                </div>
              ))}
              <div className="p-3 flex justify-between">
                <Button size="sm" variant="ghost" onClick={addLine} data-testid="party-line-add"><Plus size={14} className="mr-1" />Add line</Button>
                <div className="text-sm font-mono">Total: <span className="num">{fmtMoney(invoiceTotal, activeCompany.currency)}</span></div>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={() => submitInvoiceOrCN(openCN)} className="rounded-sm font-mono uppercase text-xs tracking-wider" data-testid="party-inv-submit">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // ---------- List View ----------
  return (
    <>
      <PageHeader
        eyebrow="Sub-ledger"
        title={plural}
        description={kind === "customer" ? "Customer accounts, invoices, credit notes, statements & receipts." : "Supplier accounts, bills, debit notes, payments & statements."}
        actions={
          <Button onClick={() => setOpenCreate(true)} data-testid={`${kind}-new-btn`} className="rounded-sm font-mono text-xs uppercase tracking-wider">
            <Plus size={14} className="mr-1" /> New {label}
          </Button>
        }
      />
      <PageBody>
        <div className="border border-border bg-card">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/40">
              <tr className="text-left">
                <Th>Name</Th>
                <Th>Email</Th>
                <Th className="text-right">{receivableLabel}</Th>
                <Th className="text-right">Open Invoices</Th>
                <Th className="text-right pr-4">Action</Th>
              </tr>
            </thead>
            <tbody>
              {list.map((p) => (
                <tr key={p.id} data-testid={`${kind}-row-${p.id}`} className="border-b border-border last:border-b-0 hover:bg-muted/40 cursor-pointer" onClick={() => loadDetail(p)}>
                  <Td className="font-mono">
                    {p.name}
                    {p.active === false && <span className="ml-2 tag-badge text-muted-foreground">inactive</span>}
                  </Td>
                  <Td className="text-muted-foreground">{p.email || "—"}</Td>
                  <Td className="text-right num">{fmtMoney(p.outstanding || 0, activeCompany.currency)}</Td>
                  <Td className="text-right num">{p.open_invoices || 0}</Td>
                  <Td className="text-right pr-4" onClick={(e) => e.stopPropagation()}>
                    <Button size="sm" variant="ghost" onClick={() => removeParty(p)} data-testid={`${kind}-del-${p.id}`}>
                      <Trash size={14} />
                    </Button>
                  </Td>
                </tr>
              ))}
              {list.length === 0 && (
                <tr><td colSpan={5} className="text-center py-8 text-muted-foreground">No {plural.toLowerCase()} yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <Dialog open={openCreate} onOpenChange={setOpenCreate}>
          <DialogContent className="rounded-sm">
            <DialogHeader>
              <DialogTitle className="font-serif-display text-2xl">New {label}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-xs uppercase tracking-widest">Name *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm font-mono" data-testid={`${kind}-create-name`} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-widest">Email</Label>
                  <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-sm font-mono" data-testid={`${kind}-create-email`} />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest">Phone</Label>
                  <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-sm font-mono" data-testid={`${kind}-create-phone`} />
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Address</Label>
                <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="rounded-sm font-mono" data-testid={`${kind}-create-address`} />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Tax ID</Label>
                <Input value={form.tax_id} onChange={(e) => setForm({ ...form, tax_id: e.target.value })} className="rounded-sm font-mono" data-testid={`${kind}-create-taxid`} />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={createParty} className="rounded-sm font-mono uppercase text-xs tracking-wider" data-testid={`${kind}-create-submit`}>Create</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </PageBody>
    </>
  );
}

const Th = ({ children, className = "" }) => (<th className={`px-4 py-3 overline ${className}`}>{children}</th>);
const Td = ({ children, className = "" }) => (<td className={`px-4 py-3 ${className}`}>{children}</td>);
const StatCard = ({ label, value, testId }) => (
  <div className="border border-border bg-card p-4" data-testid={testId}>
    <div className="overline text-muted-foreground">{label}</div>
    <div className="text-2xl font-serif-display mt-1 num">{value}</div>
  </div>
);
